import { convertFiltersToApiParams } from '@app/helpers/filter-mapping.helpers';
import { MapApiParamsInput } from '@app/models';

/**
 * Convert reservation features to booking options for the API
 */
export const convertReservationFeaturesToBookingOptions = (
    reservationFeatures?: string[],
): string[] | undefined => {
    return reservationFeatures?.map(feature => {
        switch (feature) {
            case 'realtimeBookingModel':
                return 'RealTime';
            case 'freeCancellation':
                return 'FreeCancel';
            case 'withoutPrepayment':
                return 'WithoutPrepayment';
            default:
                return feature;
        }
    });
};

/**
 * Map rating from legacy scale (0-5) to NocApi (0-10)
 * @param value
 * @returns mapped rating value
 */
export const mapRatingToApi = (value: number): number => Math.min(value * 2, 10);

/**
 * Build URL search params for the map accommodations API
 */
export const buildMapApiParams = (input: MapApiParamsInput): URLSearchParams => {
    const {
        maxLat,
        maxLon,
        minLat,
        minLon,
        checkInDate,
        checkOutDate,
        adultsCount,
        childrenCount,
        childrenAges,
        priceFrom,
        priceTo,
        propertyTypeIds,
        amenityIds,
        provisionIds,
        roomEquipmentIds,
        reservationFeatures,
        rating,
        flexDates,
    } = input;

    const convertedFilters = convertFiltersToApiParams({
        propertyTypeIds,
        amenityIds,
        provisionIds,
        roomEquipmentIds,
    });

    const bookingOptions = convertReservationFeaturesToBookingOptions(reservationFeatures);

    const params = new URLSearchParams();

    params.append('max-lat', maxLat.toString());
    params.append('max-lon', maxLon.toString());
    params.append('min-lat', minLat.toString());
    params.append('min-lon', minLon.toString());

    if (checkInDate) params.append('date-from', checkInDate);
    if (checkOutDate) params.append('date-to', checkOutDate);
    if (adultsCount) params.append('adults', adultsCount.toString());
    if (childrenCount) params.append('children', childrenCount.toString());

    if (childrenAges?.length) {
        childrenAges.forEach(age => params.append('children-age[]', age.toString()));
    }

    if (priceFrom) params.append('price-from', priceFrom.toString());
    if (priceTo) params.append('price-to', priceTo.toString());

    if (convertedFilters.propertyType?.length) {
        convertedFilters.propertyType.forEach(type => params.append('category[]', type));
    }
    if (convertedFilters.amenities?.length) {
        convertedFilters.amenities.forEach(amenity => params.append('amenity-group[]', amenity));
    }
    if (convertedFilters.provision?.length) {
        convertedFilters.provision.forEach(item => params.append('provision[]', item));
    }
    if (convertedFilters.tags?.length) {
        params.append('tags', convertedFilters.tags.join(','));
    }

    if (bookingOptions?.length) {
        bookingOptions.forEach(option => params.append('booking-options[]', option));
    }

    if (rating) params.append('rating', rating.toString());

    if (flexDates !== undefined && flexDates > 0)
        params.append('flexible-days', flexDates.toString());

    return params;
};
